import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {HttpResponseWithDataPageObject} from '../model/http-response-with-data-page-object';
import {environment} from '../../environments/environment';
import {CustomHttpResponse} from '../model/custom-http-response';
import {
  HttpResponseWithDataFullCardInfoProjectionModel
} from '../model/http-response-with-data-full-card-info-projection.model';
import {CardFilterDtoModel} from '../model/cardFilterDto.model';
import {addDays, format, subDays} from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class CardService {
  host = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getAllCard(
    cardFilterDto: CardFilterDtoModel
  ): Observable<HttpResponse<HttpResponseWithDataPageObject>> {
    const paramsObject: Record<string, string | null> = {
      identifier: cardFilterDto.identifier || null,
      cardStatus: cardFilterDto.cardStatus || null,
      cardType: cardFilterDto.cardType || null,
      cardCode: cardFilterDto.cardCode || null,
      userIdentifier: cardFilterDto.userIdentifier || null,
      userPhone: cardFilterDto.userPhone || null,
      userEmail: cardFilterDto.userEmail || null,
      page: cardFilterDto.page !== null ? cardFilterDto.page.toString() : null,
      size: cardFilterDto.size !== null ? cardFilterDto.size.toString() : null,
      sort: cardFilterDto.sort || null,
      sortDirection: cardFilterDto.sortDirection || null,
    };
    const nonNullParams = Object.fromEntries(
      Object.entries(paramsObject).filter(([_, value]) => value !== null)
    );
    let params = new HttpParams();
    // Set non-null parameters to HttpParams
    for (const [key, value] of Object.entries(nonNullParams)) {
      params = params.set(key, value as string);
    }
    return this.http.get<HttpResponseWithDataPageObject>(`${this.host}/admin/card/all`, {
      observe: 'response',
      params,
    });
  }

  getCard(id: string): Observable<HttpResponse<HttpResponseWithDataFullCardInfoProjectionModel>> {
    return this.http.get<HttpResponseWithDataFullCardInfoProjectionModel>(
      `${this.host}/admin/card/${id}`,
      { observe: 'response' }
    );
  }

  linkUser(cardId: string, userIdentifier: string): Observable<HttpResponse<CustomHttpResponse>> {
    const body = { userIdentifier };
    return this.http.patch<CustomHttpResponse>(`${this.host}/admin/card/${cardId}/link`, body, {
      observe: 'response',
    });
  }

  unlinkUser(identifier: string): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http.patch<CustomHttpResponse>(`${this.host}/admin/card/${identifier}/unlink`, '', {
      observe: 'response',
    });
  }

  importCardsSentToProduction(
    cards: { identifier: string }[]
  ): Observable<HttpResponse<CustomHttpResponse>> {
    const body = { cards };
    return this.http.patch<CustomHttpResponse>(`${this.host}/admin/card/import/production`, body, {
      observe: 'response',
    });
  }

  importCardFabricated(
    cards: { identifier: string; broken: boolean }[]
  ): Observable<HttpResponse<CustomHttpResponse>> {
    const body = { cards };
    return this.http.patch<CustomHttpResponse>(`${this.host}/admin/card/import/fabricated`, body, {
      observe: 'response',
    });
  }

  blockCard(identifier: string): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http.patch<CustomHttpResponse>(`${this.host}/admin/card/${identifier}/block`, '', {
      observe: 'response',
    });
  }

  unblockCard(identifier: string): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http.patch<CustomHttpResponse>(
      `${this.host}/admin/card/${identifier}/unblock`,
      '',
      { observe: 'response' }
    );
  }

  getCardsUser(
    code?: string,
    category?: string,
    page?: number,
    size?: number,
    sort?: string,
    sortDirection?: string
  ): Observable<HttpResponse<HttpResponseWithDataPageObject>> {
    let params = new HttpParams();
    params = params.append('page', page?.toString() || '');
    params = params.append('size', size?.toString() || '');
    if (code) {
      params = params.append('code', code);
    }
    if (category) {
      params = params.append('category', category);
    }
    if (sort) {
      params = params.append('sort', sort);
    }
    if (sortDirection) {
      params = params.append('sortDirection', sortDirection);
    }
    return this.http.get<HttpResponseWithDataPageObject>(`${this.host}/card/all`, {
      observe: 'response',
      params,
    });
  }

  getDetailsCardUser(
    cardId: string
  ): Observable<HttpResponse<HttpResponseWithDataFullCardInfoProjectionModel>> {
    return this.http.get<HttpResponseWithDataFullCardInfoProjectionModel>(
      `${this.host}/card/${cardId}`,
      { observe: 'response' }
    );
  }

  linkCard(code: string, key: string): Observable<HttpResponse<CustomHttpResponse>> {
    const body = { code, key };
    return this.http.patch<CustomHttpResponse>(`${this.host}/card/link`, body, {
      observe: 'response',
    });
  }

  updateParameters(
    cardId: string,
    parameters: { [key: string]: string }
  ): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http.patch<CustomHttpResponse>(
      `${this.host}/card/${cardId}/parameters/update`,
      parameters,
      { observe: 'response' }
    );
  }
  updateParametersByAdmin(
    cardIdentifier: string,
    parameters: { [key: string]: string }
  ): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http.patch<CustomHttpResponse>(
      `${this.host}/admin/card/${cardIdentifier}/parameters/update`,
      parameters,
      { observe: 'response' }
    );
  }

  getCategory(): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http.get<CustomHttpResponse>(`${this.host}/card/all/view/code-and-category-name`, {
      observe: 'response',
    });
  }

  getCardsWithAnalytics(
    code: string | undefined,
    category: string | undefined,
    page: number,
    size: number,
    sort: string | undefined,
    sortDirection: string | undefined,
    noCache: boolean,
    period: '7d' | '30d' | '12m' = '12m' // ADD THIS PARAMETER
  ): Observable<HttpResponse<HttpResponseWithDataPageObject>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('noCache', noCache.toString())
      .set('period', period); // ADD THIS LINE

    if (code) {
      params = params.set('code', code);
    }

    if (category) {
      params = params.set('category', category);
    }

    if (sort) {
      params = params.set('sort', sort);
    }

    if (sortDirection) {
      params = params.set('sortDirection', sortDirection);
    }

    return this.http.get<HttpResponseWithDataPageObject>(`${this.host}/card/all`, {
      params,
      observe: 'response',
    });
  }

  updateCardName(cardId: string, name: string): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http.patch<CustomHttpResponse>(`${this.host}/card/${cardId}/name`, name, {
      observe: 'response',
      headers: {'Content-Type': 'text/plain'}, // backend expects plain String
    });
  }

  getCardVisitsGraph(period: '7d' | '30d' | '12m', cardId?: string): Observable<HttpResponse<any>> {
    const now = new Date();
    let params = new HttpParams();

    if (period !== '12m') {
      const from = period === '7d' ? subDays(now, 7) : subDays(now, 30);
      params = params
        .set('from', format(from, 'yyyy/MM/dd'))
        .set('to', format(addDays(now, 1), 'yyyy/MM/dd'));
    }

    if (cardId) {
      params = params.set('cardId', cardId);
    }

    return this.http.get<any>(`${this.host}/analytics/user/card/graph`, {
      params,
      observe: 'response',
    });
  }
}
